import type {StyleBlock} from '@packages/systems/style-legacy/records';

// A guard against pathologically large style blocks chewing up memory or CPU
const MAX_STYLE_BLOCK_KEY_LENGTH = 10 * 1024; // 10 KB

/**
 * MurmurHash3 is a fast and collision-resistant, non-cryptographic hash
 * function.
 *
 * For comparison, `emotion` uses an implementation of MurmurHash2 to hash its
 * class names, so there's precedent here.
 *
 * ----
 *
 * Original implementation[1] info:
 *
 * 32-bit MurmurHash3 implemented by bryc (github.com/bryc)
 *
 * License: Public domain (or MIT if needed). Attribution appreciated.
 *
 * [1]
 * https://github.com/bryc/code/blob/88c1317ea6f9b25c153afa9c369c365fed11b482/jshash/hashes/murmurhash3.js
 */
function MurmurHash3_string(key: string, seed = 0) {
  let k: number;
  const p1 = 3432918353;
  const p2 = 461845907;
  let h = seed | 0;

  let i: number;
  // Clamp the effective length so the tail switch and final length mix below
  // stay consistent with the main loop for keys longer than the guard.
  const len = Math.min(key.length, MAX_STYLE_BLOCK_KEY_LENGTH);
  const b = len & -4;
  for (i = 0; i < b; i += 4) {
    k =
      (key.charCodeAt(i + 3) << 24) |
      (key.charCodeAt(i + 2) << 16) |
      (key.charCodeAt(i + 1) << 8) |
      key.charCodeAt(i);
    k = Math.imul(k, p1);
    k = (k << 15) | (k >>> 17);
    h ^= Math.imul(k, p2);
    h = (h << 13) | (h >>> 19);
    h = (Math.imul(h, 5) + 3864292196) | 0; // |0 = prevent float
  }

  k = 0;
  switch (len & 3) {
    case 3:
      k ^= key.charCodeAt(i + 2) << 16;
    // eslint-disable-next-line no-fallthrough
    case 2:
      k ^= key.charCodeAt(i + 1) << 8;
    // eslint-disable-next-line no-fallthrough
    case 1:
      k ^= key.charCodeAt(i);
      k = Math.imul(k, p1);
      k = (k << 15) | (k >>> 17);
      h ^= Math.imul(k, p2);
  }

  h ^= len;

  h ^= h >>> 16;
  h = Math.imul(h, 2246822507);
  h ^= h >>> 13;
  h = Math.imul(h, 3266489909);
  h ^= h >>> 16;

  return h >>> 0;
}

/**
 * Creates a string representation of style block content for hashing.
 * Simpler than getStyleBlockKey but allocates a new string each call.
 */
const getStyleBlockKey_string = (styleBlock: StyleBlock): string => {
  let result: string = '';
  result += styleBlock.styleLess;
  for (const [variantKey, variant] of styleBlock.variants) {
    const styleLess = variant.get('styleLess');
    if (!styleLess) continue;
    result += variantKey + '|' + styleLess;
    if (result.length >= MAX_STYLE_BLOCK_KEY_LENGTH) break;
  }
  return result;
};

const ELEMENT_STYLE_CLASS_NAME_PREFIX = '_';

/**
 * Creates a function that generates a class name for a given style block.
 *
 * Originally used to test hash functions and key generators to balance runtime
 * performance, memory usage, and collision resistance.
 *
 * For more details, check out the `cursor/per-page-style-system-0ed9` branch
 * and see these commits:
 *
 * - 34936f1c811506c0248a1941974f4c8990300e78
 * - 4532e9d0a81ef1a1a9b626fc96fec406526f1769
 */
const makeStyleBlockClassNameGenerator = <K>(
  keyGenerator: (styleBlock: StyleBlock) => K,
  hashGenerator: (key: K) => number
) => {
  return function getElementStyleClassName_(
    styleBlock: StyleBlock,
    hashContentSelectorsForPublishingCss: boolean
  ): string {
    if (!hashContentSelectorsForPublishingCss) {
      // The reason we use the ID here is because we really need a stable style rule
      // in the Designer to have our style update system work correctly. This also
      // allows us to update styles without re-rendering the canvas in any way, so
      // we want to keep that property, at least in the Designer.
      return ELEMENT_STYLE_CLASS_NAME_PREFIX + styleBlock._id;
    }

    const key = keyGenerator(styleBlock);
    const hash = hashGenerator(key);
    return ELEMENT_STYLE_CLASS_NAME_PREFIX + hash.toString(36);
  };
};

export const getElementStyleClassName = makeStyleBlockClassNameGenerator(
  getStyleBlockKey_string,
  MurmurHash3_string
);
